import React, {
  HTMLAttributes,
  Key,
  MouseEvent,
} from 'react';
import AddIcon from '@mui/icons-material/Add';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import clsx from 'clsx';
import AuthorizationOptionRow, {
  AuthorizationOptionRowNotes,
} from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/components/AuthorizationOptionRow';
import type {
  SelectorRow,
} from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/types';
import {
  getSelectorRowKey,
  selectorRowHighlightClasses,
} from 'pages/Dashboard/pages/Billing/ClaimForm/AuthorizationSelector/utils';

type SelectorOptionRowProps = {
  row: SelectorRow;
  listItemProps: HTMLAttributes<HTMLLIElement> & { key?: Key };
  onRowMouseDown: (event: MouseEvent) => void;
  notes: AuthorizationOptionRowNotes;
};

type SelectorActionRowProps = {
  row: Extract<SelectorRow, { kind: 'use' | 'add' }>;
  listItemProps: HTMLAttributes<HTMLLIElement> & { key?: Key };
  onRowMouseDown: (event: MouseEvent) => void;
};

function SelectorActionRow({
  row,
  listItemProps,
  onRowMouseDown,
}: SelectorActionRowProps) {
  const Icon = row.kind === 'use' ? KeyboardReturnIcon : AddIcon;

  return (
    <li
      {...listItemProps}
      key={listItemProps.key}
      role="option"
      aria-selected={listItemProps['aria-selected'] ?? false}
      className={clsx(
        listItemProps.className,
        '!flex !w-full !items-center !gap-2 !border-b !border-gray-100 !px-3 !py-2 !text-sm !font-semibold',
        selectorRowHighlightClasses,
      )}
      onMouseDown={onRowMouseDown}
    >
      <Icon className="!h-4 !w-4 shrink-0" />
      <span>
        {row.kind === 'use' ? (
          <>
            Use &quot;{row.typedNumber}&quot;
          </>
        ) : (
          <>
            Add authorization {row.typedNumber}
          </>
        )}
      </span>
    </li>
  );
}

export default function SelectorOptionRow({
  row,
  listItemProps,
  onRowMouseDown,
  notes,
}: SelectorOptionRowProps) {
  return row.kind === 'authorization' ? (
    <AuthorizationOptionRow
      option={row.option}
      listItemProps={listItemProps}
      onRowMouseDown={onRowMouseDown}
      notes={notes}
    />
  ) : (
    <SelectorActionRow
      row={row}
      listItemProps={{
        ...listItemProps,
        key: getSelectorRowKey(row),
      }}
      onRowMouseDown={onRowMouseDown}
    />
  );
}
